/** Formati italiani dei campi data/ora. Il valore verso le pagine resta quello ISO dell'<input> nativo. */
export type DateKind = 'date' | 'month' | 'time' | 'datetime-local'

export const DATE_KINDS: readonly string[] = ['date', 'month', 'time', 'datetime-local']

/** Maschera: `d` = una cifra, altri caratteri = separatori. */
export const MASKS: Record<DateKind, string> = {
  date: 'dd/dd/dddd',
  month: 'dd/dddd',
  time: 'dd:dd',
  'datetime-local': 'dd/dd/dddd dd:dd',
}
export const PLACEHOLDERS: Record<DateKind, string> = {
  date: 'gg/mm/aaaa',
  month: 'mm/aaaa',
  time: 'hh:mm',
  'datetime-local': 'gg/mm/aaaa hh:mm',
}

const p2 = (n: number) => String(n).padStart(2, '0')
export const isoDate = (y: number, m: number, d: number) => `${String(y).padStart(4, '0')}-${p2(m)}-${p2(d)}`
export const daysInMonth = (y: number, m: number) => new Date(y, m, 0).getDate()

export function applyMask(raw: string, mask: string): string {
  const digits = raw.replace(/\D/g, '')
  let out = ''
  let di = 0
  for (let i = 0; i < mask.length && di < digits.length; i += 1) {
    if (mask[i] === 'd') {
      out += digits[di]
      di += 1
    } else {
      out += mask[i]
    }
  }
  return out
}

function validDate(y: number, m: number, d: number) {
  return y >= 1 && m >= 1 && m <= 12 && d >= 1 && d <= daysInMonth(y, m)
}

/** Da ISO a testo italiano ('' se non riconosciuto). */
export function formatValue(kind: DateKind, iso: string): string {
  if (!iso) return ''
  if (kind === 'date') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
    return m ? `${m[3]}/${m[2]}/${m[1]}` : ''
  }
  if (kind === 'month') {
    const m = /^(\d{4})-(\d{2})/.exec(iso)
    return m ? `${m[2]}/${m[1]}` : ''
  }
  if (kind === 'time') {
    const m = /^(\d{2}):(\d{2})/.exec(iso)
    return m ? `${m[1]}:${m[2]}` : ''
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso)
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : ''
}

/** Dal testo (completo) a ISO; null se incompleto o impossibile. */
export function parseText(kind: DateKind, text: string): string | null {
  if (kind === 'date') {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text)
    if (!m) return null
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])]
    return validDate(y, mo, d) ? isoDate(y, mo, d) : null
  }
  if (kind === 'month') {
    const m = /^(\d{2})\/(\d{4})$/.exec(text)
    if (!m) return null
    const [mo, y] = [Number(m[1]), Number(m[2])]
    return validDate(y, mo, 1) ? `${String(y).padStart(4, '0')}-${p2(mo)}` : null
  }
  if (kind === 'time') {
    const m = /^(\d{2}):(\d{2})$/.exec(text)
    if (!m) return null
    return Number(m[1]) < 24 && Number(m[2]) < 60 ? text : null
  }
  const m = /^(\d{2}\/\d{2}\/\d{4}) (\d{2}:\d{2})$/.exec(text)
  if (!m) return null
  const d = parseText('date', m[1] ?? '')
  const t = parseText('time', m[2] ?? '')
  return d && t ? `${d}T${t}` : null
}

/** Confronto con min/max (stesso formato del valore). */
export function outOfRange(kind: DateKind, iso: string, min?: string, max?: string): boolean {
  const cut = (v: string) => (kind === 'time' ? v.slice(0, 5) : kind === 'month' ? v.slice(0, 7) : kind === 'date' ? v.slice(0, 10) : v.slice(0, 16))
  const v = cut(iso)
  if (min && v < cut(min)) return true
  if (max && v > cut(max)) return true
  return false
}

export const todayIso = () => {
  const n = new Date()
  return isoDate(n.getFullYear(), n.getMonth() + 1, n.getDate())
}

const MONTHS = Array.from({ length: 12 }, (_, i) => {
  const s = new Intl.DateTimeFormat('it-IT', { month: 'long' }).format(new Date(2024, i, 1))
  return s.charAt(0).toUpperCase() + s.slice(1)
})
export const monthName = (m: number) => MONTHS[m - 1] ?? ''
export const monthShort = (m: number) => monthName(m).slice(0, 3)
export const WEEKDAYS_NARROW = ['L', 'M', 'M', 'G', 'V', 'S', 'D']
export const WEEKDAYS_LONG = ['lunedì', 'martedì', 'mercoledì', 'giovedì', 'venerdì', 'sabato', 'domenica']
